// Catalog.ID Client — Key Grant Cryptographic Primitives
// ============================================================================
//
// A Key Grant answer that carries a secret wraps it for delivery to one
// specific requester using ephemeral ECDH + AES-256-GCM. We use P-256 ECDH
// via WebCrypto (no extra dep, universally available) + HKDF-SHA256 + AES-GCM.
// Since cid-encrypted-storage M6b the one such answer is the
// `bitcash-auth-token` release (registration-wallets §4.10), which wraps a
// bounded spending token rather than a key bag entry.
//
// Protocol (responder side, implemented here):
//   1. Receive requester's ephemeralPubkey (raw uncompressed P-256, base64).
//   2. Generate our own ephemeral P-256 keypair.
//   3. ECDH(our_priv, their_pub) → raw shared bits.
//   4. HKDF-SHA256(bits, info = "catalog-id-key-grant-v1", salt = "") → 32-byte AES-256 key.
//   5. AES-GCM(aesKey, nonce=random(12), aad="key-grant-response") on the plaintext private key.
//   6. Send back {cidEphemeralPubkey, nonce, ciphertext, tag} in the response.
//
// The requester performs the symmetric ECDH on their side and decrypts.
//
// The reverse direction — `unwrapFromPeer`, for the retired `save` mode, where
// a requester wrapped a secret to a pubkey we published on `save-prepare` —
// went with that mode in M6b. Nothing hands this vault plaintext through a
// key-grant envelope any more; the bitcash wallet document, the one plaintext
// that still arrives, travels the hybrid-KEM path in
// `bitcash-wallets-save-store.ts`.
// ============================================================================

const KDF_INFO = new TextEncoder().encode('catalog-id-key-grant-v1');
const AAD = new TextEncoder().encode('key-grant-response');

function base64Encode(bytes: Uint8Array): string {
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin);
}

function base64Decode(s: string): Uint8Array {
    const bin = atob(s);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
}

/**
 * WebCrypto's BufferSource-typed parameters don't accept a `Uint8Array` whose
 * backing buffer is typed as `ArrayBufferLike` (which can be a SharedArrayBuffer)
 * under strict TS. Copying into a fresh ArrayBuffer sidesteps the variance issue
 * without runtime cost beyond the copy (bytes are already local).
 */
function toArrayBuffer(u8: Uint8Array): ArrayBuffer {
    const ab = new ArrayBuffer(u8.byteLength);
    new Uint8Array(ab).set(u8);
    return ab;
}

async function deriveAesKey(theirPubRaw: Uint8Array, ourPriv: CryptoKey): Promise<CryptoKey> {
    const theirPub = await crypto.subtle.importKey(
        'raw',
        toArrayBuffer(theirPubRaw),
        { name: 'ECDH', namedCurve: 'P-256' },
        false,
        []
    );
    const sharedBits = await crypto.subtle.deriveBits(
        { name: 'ECDH', public: theirPub },
        ourPriv,
        256
    );
    const hkdfKey = await crypto.subtle.importKey(
        'raw',
        sharedBits,
        'HKDF',
        false,
        ['deriveKey']
    );
    return await crypto.subtle.deriveKey(
        { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(0), info: KDF_INFO },
        hkdfKey,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
    );
}

export interface WrappedKey {
    /** Our ephemeral P-256 public key (raw, uncompressed, base64). */
    cidEphemeralPubkey: string;
    /** 12-byte AES-GCM nonce, base64. */
    nonce: string;
    /** AES-GCM(ciphertext || tag), base64. */
    ciphertext: string;
}

/**
 * Wrap a private-key payload for a specific requester.
 *
 * @param requesterEphemeralPubkeyB64 requester's raw uncompressed P-256 pubkey, base64
 * @param plaintext the key material to release (e.g. the base64-decoded key bag entry)
 */
export async function wrapForRequester(requesterEphemeralPubkeyB64: string, plaintext: Uint8Array): Promise<WrappedKey> {
    const theirPubRaw = base64Decode(requesterEphemeralPubkeyB64);
    if (theirPubRaw.length !== 65 || theirPubRaw[0] !== 0x04) {
        throw new Error('Invalid ephemeralPubkey: expected 65-byte uncompressed P-256 point');
    }

    // Generate our own ephemeral keypair (extractable=true so we can export the public half).
    const ourPair = await crypto.subtle.generateKey(
        { name: 'ECDH', namedCurve: 'P-256' },
        true,
        ['deriveKey', 'deriveBits']
    );

    const ourPubRaw = new Uint8Array(await crypto.subtle.exportKey('raw', ourPair.publicKey));
    const aesKey = await deriveAesKey(theirPubRaw, ourPair.privateKey);

    const nonce = crypto.getRandomValues(new Uint8Array(12));
    const cipherAndTag = new Uint8Array(await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: toArrayBuffer(nonce), additionalData: toArrayBuffer(AAD) },
        aesKey,
        toArrayBuffer(plaintext)
    ));

    return {
        cidEphemeralPubkey: base64Encode(ourPubRaw),
        nonce: base64Encode(nonce),
        ciphertext: base64Encode(cipherAndTag),
    };
}

/** Encode raw bytes as base64 — exported for client glue. */
export function bytesToBase64(bytes: Uint8Array): string { return base64Encode(bytes); }

